import React, { useState, useEffect } from "react";
import {
  User,
  Mail,
  Phone,
  Banknote,
  ShieldCheck,
  Building2,
  Edit3,
  Save,
  UserCheck,
  AlertCircle,
  Loader2,
  Plane,
  CreditCard,
  Percent,
  MapPin,
  Clock
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { UserDto, ListingPlanDto } from "@/types";
import { useAppContext } from "@/context/AppContext";
import { message } from "antd";
import { Button } from "@/components/ui/button";
import { AppService } from "../services/appService";

const InputField = "w-full h-11 px-4 rounded-xl bg-[#1E293B] border border-[#334155] text-white placeholder:text-slate-500 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all outline-none disabled:opacity-50 disabled:cursor-not-allowed";
const Label = ({ children, className = "" }: { children: React.ReactNode, className?: string }) => (
  <span className={`text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5 block ml-1 ${className}`}>
    {children}
  </span>
);

const GlassCard = ({ children, title, icon: Icon, className = "" }: any) => (
  <div className={`bg-[#0F172A]/80 backdrop-blur-xl border border-slate-800 rounded-[2.5rem] p-8 shadow-2xl ${className}`}>
    {title && (
      <div className="flex items-center gap-3 mb-6 border-b border-slate-800/50 pb-4">
        {Icon && <Icon size={18} className="text-blue-400" />}
        <h3 className="text-xs font-black uppercase tracking-[0.2em] text-white italic">{title}</h3>
      </div>
    )}
    {children}
  </div>
);

const MyAccount = () => {
  const {
    currentUser,
    refreshUser,
    listingPlans = [],
    aircraftCommissionRate = 0,
    rentalCommissionRate = 0,
    partCommissionRate = 0,
    experienceCommissionRate = 0
  } = useAuth();

  const { configureSellerAccount, updateUserAccount } = useAppContext();
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingSeller, setIsEditingSeller] = useState(false);
  const [formData, setFormData] = useState<UserDto | null>(null);

  // Seller Setup State
  const [isFirm, setIsFirm] = useState<boolean | null>(null);

  // ACC-VALIDATION STATES
  const [ibanValid, setIbanValid] = useState(true);
  const [vatValid, setVatValid] = useState(true);
  const [isValidatingIban, setIsValidatingIban] = useState(false);
  const [isValidatingVat, setIsValidatingVat] = useState(false);

  useEffect(() => {
    if (currentUser) {
      setFormData({
        ...currentUser,
      });

      if (currentUser.companyName) {
        setIsFirm(true);
      } else if (currentUser.iban) {
        setIsFirm(false);
      }
    }
  }, [currentUser]);

  // DEBOUNCED INTERCEPTORS FOR ASYNC SERVER-SIDE LOGIC
  useEffect(() => {
    if (!formData?.iban || formData.iban.trim() === "" || !isEditingSeller) {
      setIbanValid(true);
      return;
    }

    setIsValidatingIban(true);
    const timeout = setTimeout(async () => {
      try {
        const isValid = await AppService.validateIban(formData.iban);
        setIbanValid(isValid);
      } catch (err) {
        setIbanValid(false);
      } finally {
        setIsValidatingIban(false);
      }
    }, 500);

    return () => clearTimeout(timeout);
  }, [formData?.iban, isEditingSeller]);

  useEffect(() => {
    if (!isFirm || !formData?.vatId || formData.vatId.trim() === "" || !isEditingSeller) {
      setVatValid(true);
      return;
    }

    setIsValidatingVat(true);
    const timeout = setTimeout(async () => {
      try {
        const isValid = await AppService.validateVatId(formData.vatId);
        setVatValid(isValid);
      } catch (err) {
        setVatValid(false);
      } finally {
        setIsValidatingVat(false);
      }
    }, 500);

    return () => clearTimeout(timeout);
  }, [formData?.vatId, isFirm, isEditingSeller]);

  if (!currentUser || !formData) return null;

  const handleConfigureSeller = async () => {
    if (!ibanValid || (isFirm && !vatValid)) {
      message.error("Vă rugăm să corectați erorile înainte de salvare.");
      return;
    }

    setLoading(true);
    try {
      await configureSellerAccount(formData);
      await refreshUser();
      setIsEditingSeller(false);
      setIsFirm(null);
      message.success("Configurarea a fost salvată cu succes!");
    } catch (error) {
      message.error("Eroare întâmpinată la salvarea configurării de seller.");
    } finally {
      setLoading(false);
    }
  };

    const getDurationLabel = (duration?: string) => {
      if (!duration) return "perioadă";
      const d = duration.toUpperCase();
      if (d === "MONTHLY") return "lună";
      if (d === "QUARTERLY") return "trimestru";
      if (d === "YEARLY") return "an";
      return duration.toLowerCase();
    };

  const handleUpdate = async () => {
    setLoading(true);
    try {
      await updateUserAccount(formData);
      setIsEditing(false);
    } catch (err) {
      message.error("Eroare la actualizarea profilului.");
    } finally {
      setLoading(false);
    }
  };

  const needsSellerSetup = currentUser.seller && !currentUser.iban;
  const canEditSeller = needsSellerSetup || isEditingSeller;

  return (
    <div className="min-h-screen bg-[#020617] text-slate-300 pb-20 font-sans">
      <div className="container max-w-5xl py-12 px-6">

        {/* HEADER */}
        <header className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 border-l-4 border-blue-600 pl-6">
          <div>
            <p className="text-blue-400 font-mono text-sm tracking-[0.2em] mb-1">TERMINAL UTILIZATOR // PROFILE</p>
            <h1 className="text-5xl font-black text-white tracking-tight italic">
              Contul <span className="text-slate-500">Meu</span>
            </h1>
          </div>
          <div className="flex items-center gap-4 bg-slate-900/50 p-3 rounded-2xl border border-slate-800">
            <div className="w-10 h-10 bg-blue-600/20 rounded-xl flex items-center justify-center border border-blue-500/30">
               <UserCheck className="text-blue-400" size={20} />
            </div>
            <div>
                <p className="text-[10px] text-slate-500 uppercase font-bold tracking-widest leading-none">Identitate</p>
                <p className="text-white font-mono text-sm font-bold">@{currentUser.userName}</p>
            </div>
          </div>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">

          {/* LEFT COL: INFO CARDS */}
          <div className="md:col-span-2 space-y-8">

            {needsSellerSetup && (
              <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-[2rem] p-6 animate-pulse shadow-[0_0_20px_rgba(16,185,129,0.1)]">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-emerald-500/20 rounded-2xl">
                    <AlertCircle className="text-emerald-400" />
                  </div>
                  <div>
                    <h4 className="text-emerald-400 font-black uppercase text-sm italic tracking-widest">Configurare Cont Seller</h4>
                    <p className="text-xs text-slate-400 mt-1">Ești înregistrat ca vânzător, dar nu ai configurat detaliile de plată. Configurează IBAN-ul pentru a putea lista aeronave.</p>
                  </div>
                </div>
              </div>
            )}

            {/* PERSONAL INFO CARD */}
            <GlassCard title="Informații Personale" icon={User}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label>Nume</Label>
                  <input
                    className={InputField}
                    value={formData.lastName}
                    disabled={!isEditing}
                    onChange={e => setFormData({...formData, lastName: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Prenume</Label>
                  <input
                    className={InputField}
                    value={formData.firstName}
                    disabled={!isEditing}
                    onChange={e => setFormData({...formData, firstName: e.target.value})}
                  />
                </div>
                <div className="md:col-span-2">
                  <Label>Email System</Label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-3.5 text-slate-500" size={16} />
                    <input
                      className={InputField + " pl-12"}
                      value={formData.email}
                      disabled={!isEditing}
                      onChange={e => setFormData({...formData, email: e.target.value})}
                    />
                  </div>
                </div>
                <div className="md:col-span-2">
                  <Label>Contact Telefon</Label>
                  <div className="relative">
                    <Phone className="absolute left-4 top-3.5 text-slate-500" size={16} />
                    <input
                      className={InputField + " pl-12"}
                      value={formData.phone}
                      disabled={!isEditing}
                      onChange={e => setFormData({...formData, phone: e.target.value})}
                    />
                  </div>
                </div>
                <div className="md:col-span-2">
                  <Label>Adresă</Label>
                  <div className="relative">
                    <MapPin className="absolute left-4 top-3.5 text-slate-500" size={16} />
                    <input
                      className={InputField + " pl-12"}
                      value={formData.address || ""}
                      disabled={!isEditing}
                      placeholder="Introduceți adresa completă"
                      onChange={e => setFormData({...formData, address: e.target.value})}
                    />
                  </div>
                </div>
              </div>

              <div className="mt-8 flex justify-end">
                {!isEditing ? (
                  <Button
                    onClick={() => setIsEditing(true)}
                    className="bg-slate-800 hover:bg-slate-700 text-white rounded-xl font-bold uppercase text-[10px] tracking-widest h-11 px-6 flex items-center gap-2"
                  >
                    <Edit3 size={14} /> Editează Profilul
                  </Button>
                ) : (
                  <div className="flex gap-4">
                    <Button
                      variant="ghost"
                      onClick={() => { setIsEditing(false); setFormData(currentUser); }}
                      className="text-slate-500 font-bold uppercase text-[10px]"
                    >
                      Anulează
                    </Button>
                    <Button
                      onClick={handleUpdate}
                      disabled={loading}
                      className="bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-bold uppercase text-[10px] tracking-widest h-11 px-8 flex items-center gap-2"
                    >
                      {loading ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Salvează
                    </Button>
                  </div>
                )}
              </div>
            </GlassCard>

            {/* SELLER DATA SECTION */}
            {currentUser.seller && (
              <>
                <GlassCard title="Configurare Seller / Business" icon={Banknote}>
                  {canEditSeller && isFirm === null ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <button
                        onClick={() => setIsFirm(false)}
                        className="p-6 rounded-3xl border border-slate-800 bg-slate-900/40 hover:border-blue-500/50 transition-all text-left group"
                      >
                        <User className="text-slate-500 group-hover:text-blue-400 mb-4" />
                        <h4 className="text-white font-black uppercase text-xs italic tracking-widest">Persoană Fizică</h4>
                        <p className="text-[10px] text-slate-500 mt-1 font-mono uppercase">Vânzător Privat / Independent</p>
                      </button>
                      <button
                        onClick={() => setIsFirm(true)}
                        className="p-6 rounded-3xl border border-slate-800 bg-slate-900/40 hover:border-emerald-500/50 transition-all text-left group"
                      >
                        <Building2 className="text-slate-500 group-hover:text-emerald-400 mb-4" />
                        <h4 className="text-white font-black uppercase text-xs italic tracking-widest">Firma / Juridică</h4>
                        <p className="text-[10px] text-slate-500 mt-1 font-mono uppercase">Companie / PFA / Entitate Fiscală</p>
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {isFirm && (
                          <>
                            <div className="md:col-span-2">
                              <Label>Nume Companie / Firmă</Label>
                              <input
                                className={InputField}
                                placeholder="EX: HangarX Aviation SRL"
                                value={formData.companyName || ""}
                                disabled={!canEditSeller}
                                onChange={e => setFormData({...formData, companyName: e.target.value})}
                              />
                            </div>
                            <div className="md:col-span-2 relative">
                              <Label>Cod Unic Identificare (CUI / VAT)</Label>
                              <div className="relative flex items-center">
                                <input
                                  className={`${InputField} ${!vatValid && formData.vatId ? "border-red-500 focus:border-red-500 focus:ring-red-500/50" : ""}`}
                                  placeholder="RO 12345678"
                                  value={formData.vatId || ""}
                                  disabled={!canEditSeller}
                                  onChange={e => setFormData({...formData, vatId: e.target.value})}
                                />
                                {isValidatingVat && (
                                  <Loader2 className="absolute right-4 animate-spin text-blue-400 h-4 w-4" />
                                )}
                              </div>
                              {!vatValid && formData.vatId && (
                                <p className="text-xs text-red-500 mt-1.5 pl-1 flex items-center gap-1">CUI / Cod VAT invalid sau sintaxă incorectă</p>
                              )}
                            </div>
                          </>
                        )}

                        <div className="md:col-span-2 relative">
                          <Label>IBAN pentru Decontare</Label>
                          <div className="relative flex items-center">
                            <input
                              className={`${InputField} ${!ibanValid && formData.iban ? "border-red-500 focus:border-red-500 focus:ring-red-500/50" : ""}`}
                              placeholder="RO00 BTRL XXXX XXXX XXXX XXXX"
                              value={formData.iban || ""}
                              disabled={!canEditSeller}
                              onChange={e => setFormData({...formData, iban: e.target.value})}
                            />
                            {isValidatingIban && (
                              <Loader2 className="absolute right-4 animate-spin text-blue-400 h-4 w-4" />
                            )}
                          </div>
                          {!ibanValid && formData.iban && (
                            <p className="text-xs text-red-500 mt-1.5 pl-1 flex items-center gap-1">Cont IBAN invalid sau structură incorectă</p>
                          )}
                        </div>
                      </div>

                      {!canEditSeller ? (
                        <div className="flex justify-end">
                           <Button
                            onClick={() => {
                              setIsEditingSeller(true);
                              setIsFirm(null);
                            }}
                            className="bg-slate-800 hover:bg-slate-700 text-white rounded-xl font-bold uppercase text-[10px] tracking-widest h-11 px-6 flex items-center gap-2"
                          >
                            <Edit3 size={14} /> Editează Date Seller
                          </Button>
                        </div>
                      ) : (
                        <div className="flex flex-col gap-4">
                          <Button
                            onClick={handleConfigureSeller}
                            disabled={
                              loading ||
                              !formData.iban ||
                              !ibanValid ||
                              (isFirm && !vatValid) ||
                              isValidatingIban ||
                              isValidatingVat
                            }
                            className="w-full bg-emerald-600 hover:bg-emerald-500 text-white rounded-2xl h-14 font-black uppercase italic tracking-tighter transition-all shadow-xl shadow-emerald-900/20 disabled:opacity-50"
                          >
                            {loading ? <Loader2 className="animate-spin" /> : "Finalizează Configurare Seller"}
                          </Button>
                          <button
                            type="button"
                            onClick={() => {
                              setIsEditingSeller(false);
                              setFormData(currentUser);
                              setIbanValid(true);
                              setVatValid(true);
                              if (currentUser.companyName) setIsFirm(true);
                              else if (currentUser.iban) setIsFirm(false);
                            }}
                            className="w-full text-[10px] font-bold text-slate-600 uppercase hover:text-slate-400 transition-colors"
                          >
                            Anulează Editarea
                          </button>
                        </div>
                      )}

                      {canEditSeller && (
                        <button
                          type="button"
                          onClick={() => setIsFirm(null)}
                          className="w-full text-[10px] font-bold text-slate-600 uppercase hover:text-slate-400 transition-colors"
                        >
                          Schimbă tip cont (Persoană / Firmă)
                        </button>
                      )}
                    </div>
                  )}
                </GlassCard>

                {/* PRICING MODELS CARD (SEPARATE GLASSCARD) */}
                <GlassCard title="Modele de Tarifare Disponibile" icon={Percent}>
                  <div className="mb-6 rounded-2xl bg-slate-900/50 border border-slate-800 p-5">
                    <p className="text-slate-400 text-xs leading-relaxed">
                      Opțiunile de mai jos au rol informativ. Modelul de facturare dorit se va selecta individual <strong className="text-blue-400">pe fiecare anunț publicat</strong> în platformă.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                    <div className="p-5 rounded-xl border border-slate-800 bg-slate-950/40 space-y-4">
                      <div className="flex items-center gap-2 text-blue-400 font-bold text-[11px] uppercase tracking-wider">
                        <CreditCard size={14} />
                        <span>Opțiunea 1: Planuri de Anunt</span>
                      </div>
                      <div className="grid grid-cols-1 gap-3">
                        {listingPlans.length > 0 ? (
                          listingPlans.map((plan: ListingPlanDto) => (
                            <div
                              key={plan.planId || plan.planName}
                              className="p-4 rounded-xl border border-slate-800/60 bg-slate-900/20 flex flex-col justify-between"
                            >
                              <div>
                                <div className="flex items-center justify-between border-b border-slate-800/60 pb-1.5 mb-2">
                                  <p className="text-white font-bold text-xs uppercase tracking-wide">
                                    {plan.planName}
                                  </p>
                                  {plan.planDuration && (
                                    <span className="flex items-center gap-1 text-[9px] font-mono uppercase bg-blue-500/10 text-blue-400 px-2 py-0.5 rounded-md border border-blue-500/20">
                                      <Clock size={10} /> {plan.planDuration}
                                    </span>
                                  )}
                                </div>
                                <p className="text-[11px] text-slate-400 leading-normal">
                                  {plan.description}
                                </p>
                              </div>
                              <div className="mt-4 pt-2 border-t border-slate-900/60 flex items-baseline justify-between font-mono">
                                <span className="text-[9px] text-slate-500">ID: #{plan.planId}</span>
                                <span className="text-sm font-black text-white">
                                  {plan.rate} EUR
                                  <span className="text-[10px] font-normal text-slate-500">
                                    /{getDurationLabel(plan.planDuration)}
                                  </span>
                                </span>
                              </div>
                            </div>
                          ))
                        ) : (
                          <p className="text-xs text-slate-500 italic">
                            Nu s-au găsit planuri de publicare active.
                          </p>
                        )}
                      </div>
                    </div>

                    {/* COMMISSION DISPLAY FROM DIRECT VARIABLES */}
                    <div className="p-5 rounded-xl border border-slate-800 bg-slate-950/40 space-y-4">
                      <div className="flex items-center gap-2 text-emerald-400 font-bold text-[11px] uppercase tracking-wider">
                        <Percent size={14} />
                        <span>Opțiunea 2: Comision la Tranzacție</span>
                      </div>
                      <div className="p-4 rounded-xl border border-slate-800/60 bg-slate-900/20 space-y-3">
                        <p className="text-white font-bold text-xs uppercase tracking-wide border-b border-slate-800 pb-1">
                          Grilă Standard Procente
                        </p>
                        <div className="text-[11px] text-slate-400 space-y-2 font-mono">
                          <div className="flex justify-between border-b border-slate-800/40 pb-1">
                            <span>Vânzare Avion:</span>
                            <span className="text-white font-bold">{aircraftCommissionRate * 100}%</span>
                          </div>
                          <div className="flex justify-between border-b border-slate-800/40 pb-1">
                            <span>Închiriere Flotă:</span>
                            <span className="text-white font-bold">{rentalCommissionRate * 100}%</span>
                          </div>
                          <div className="flex justify-between border-b border-slate-800/40 pb-1">
                            <span>Piese / Componente:</span>
                            <span className="text-white font-bold">{partCommissionRate * 100}%</span>
                          </div>
                          <div className="flex justify-between pb-1">
                            <span>Experiențe Aviație:</span>
                            <span className="text-white font-bold">{experienceCommissionRate * 100}%</span>
                          </div>
                        </div>
                      </div>
                      <p className="text-[10px] text-slate-500 italic pl-1 leading-normal">
                        * Zero costuri fixe. Comisioanele se aplică exclusiv valorilor încasate la decontare.
                      </p>
                    </div>
                  </div>
                </GlassCard>
              </>
            )}
          </div>

          {/* RIGHT COL: SIDEBAR */}
          <div className="space-y-8">
            <GlassCard className="bg-gradient-to-br from-blue-600/10 to-transparent">
              <div className="text-center">
                <div className="w-20 h-20 bg-blue-600 rounded-[2rem] mx-auto flex items-center justify-center shadow-lg shadow-blue-600/30 mb-4">
                  <Plane className="text-white" size={32} />
                </div>
                <h2 className="text-white font-black uppercase tracking-widest text-sm italic">HangarX Member</h2>
                <p className="text-[10px] text-blue-500 font-mono mt-1">STATUS: ACTIVE // L3 SECURED</p>
              </div>
              <div className="mt-8 space-y-4">
                <div className="flex justify-between items-center border-b border-slate-800 pb-2">
                   <span className="text-[10px] uppercase font-bold text-slate-500">Membru Din</span>
                   <span className="text-xs text-white font-mono">{formData.memberFrom}</span>
                </div>
                <div className="flex justify-between items-center border-b border-slate-800 pb-2">
                   <span className="text-[10px] uppercase font-bold text-slate-500">Tip Cont</span>
                   <span className="text-xs text-white font-mono uppercase">{currentUser.seller ? 'Seller Hub' : 'Cumpărător'}</span>
                </div>
              </div>
            </GlassCard>

            <GlassCard className="border-dashed">
              <div className="flex items-center gap-3 mb-4">
                <ShieldCheck className="text-slate-500" size={18} />
                <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-500 italic">Securitate Date</h3>
              </div>
              <p className="text-[10px] text-slate-600 leading-relaxed font-mono">
                Toate tranzacțiile financiare și datele de facturare sunt criptate.
                HangarX nu stochează parolele în format text.
              </p>
            </GlassCard>
          </div>

        </div>
      </div>
    </div>
  );
};

export default MyAccount;
